import { useEffect, useRef, useState } from "react";

import {
  detect,
  drain,
  handleEvent,
  INITIAL_DECODER_STATE,
  INITIAL_DETECTOR_STATE,
  unitMs,
  type DetectorConfig,
  type DetectorState,
  type StreamingDecoderConfig,
  type StreamingDecoderState,
} from "@nav-games/morse";

/// Listen to the microphone, detect a Morse tone in the audio, decode
/// it to text in (near-)real time. The pure-logic detector +
/// streamingDecoder live in `@nav-games/morse`; this component wires
/// them to Web Audio + getUserMedia and renders the running output.
///
/// `requestAnimationFrame` drives the analysis at ~60 fps. With a
/// 2048-sample buffer at 48 kHz, each frame inspects ~43 ms of audio.
/// That's tight enough that a 60ms dit (20 WPM) only spans two frames
/// — well within the detector's resolution.
///
/// Permission is granted once per page load. If the user denies, we
/// show the prompt and let them retry; the worker doesn't need an
/// always-on mic.

export function MicDemo() {
  const [wpm, setWpm] = useState(20);
  const [threshold, setThreshold] = useState(0.04);
  const [output, setOutput] = useState("");
  const [level, setLevel] = useState(0);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Audio + analysis state held in refs so the requestAnimationFrame
  // loop can read/write without triggering re-renders on each tick.
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const detectorStateRef = useRef<DetectorState>(INITIAL_DETECTOR_STATE);
  const decoderStateRef = useRef<StreamingDecoderState>(INITIAL_DECODER_STATE);

  // Re-read the config sliders on every animation frame via refs so
  // the user can tweak WPM mid-stream without restarting the listener.
  const wpmRef = useRef(wpm);
  const thresholdRef = useRef(threshold);
  useEffect(() => {
    wpmRef.current = wpm;
  }, [wpm]);
  useEffect(() => {
    thresholdRef.current = threshold;
  }, [threshold]);

  // Cleanup on unmount or stop.
  useEffect(() => {
    return () => {
      stopInternal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopInternal = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (ctxRef.current && ctxRef.current.state !== "closed") {
      void ctxRef.current.close();
    }
    ctxRef.current = null;
    analyserRef.current = null;
  };

  const stop = () => {
    stopInternal();
    setListening(false);
    setLevel(0);
  };

  const start = async () => {
    setError(null);
    setOutput("");
    detectorStateRef.current = INITIAL_DETECTOR_STATE;
    decoderStateRef.current = INITIAL_DECODER_STATE;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;
      const ctx = new AudioContext();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;
      source.connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);
      setListening(true);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getFloatTimeDomainData(buffer);
        const now = performance.now();
        const detectorConfig: DetectorConfig = {
          threshold: thresholdRef.current,
          hysteresis: thresholdRef.current * 0.5,
        };
        const decoderConfig: StreamingDecoderConfig = {
          unitMs: unitMs(wpmRef.current),
        };
        const step = detect(buffer, now, detectorStateRef.current, detectorConfig);
        detectorStateRef.current = step.state;
        setLevel(step.state.lastRms);
        let dState = decoderStateRef.current;
        for (const event of step.events) {
          dState = handleEvent(event, dState, decoderConfig);
        }
        dState = drain(now, dState, decoderConfig);
        if (dState !== decoderStateRef.current) {
          decoderStateRef.current = dState;
          setOutput(dState.output);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setError((e as Error).message || "microphone access denied");
      stopInternal();
      setListening(false);
    }
  };

  return (
    <div className="card">
      <h2>Morse — listen</h2>
      <p className="muted small">
        Point your phone at a Morse source (or play the encoder above
        next to it) and watch the decoded text appear here.
      </p>

      <div className="row" style={{ marginBottom: 12, alignItems: "center", gap: 16 }}>
        <label className="small muted" htmlFor="mic-wpm">
          Expected WPM
        </label>
        <input
          id="mic-wpm"
          type="number"
          min={5}
          max={40}
          value={wpm}
          onChange={(e) => setWpm(Math.max(1, Number(e.target.value) || 20))}
          style={{ width: 80 }}
        />
        <label className="small muted" htmlFor="mic-threshold" style={{ marginLeft: 8 }}>
          Threshold
        </label>
        <input
          id="mic-threshold"
          type="range"
          min={0.01}
          max={0.5}
          step={0.005}
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          style={{ flex: 1 }}
        />
        <span className="small muted" style={{ minWidth: 56, textAlign: "right" }}>
          {threshold.toFixed(3)}
        </span>
      </div>

      <div className="row" style={{ marginBottom: 12 }}>
        {listening ? (
          <button className="secondary" onClick={stop}>
            Stop
          </button>
        ) : (
          <button onClick={start}>Start listening</button>
        )}
      </div>

      {/* Level meter — shows the live RMS so the user can confirm the
          mic is picking up audio and tune the threshold. */}
      <div
        style={{
          height: 10,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          overflow: "hidden",
          marginBottom: 12,
        }}
        aria-hidden="true"
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(100, level * 100 / Math.max(0.001, threshold * 2))}%`,
            background: level > threshold ? "var(--accent)" : "var(--text-dim)",
            transition: "width 30ms linear",
          }}
        />
      </div>

      <pre
        style={{
          margin: 0,
          minHeight: 60,
          padding: 12,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          fontFamily: "ui-monospace, monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "var(--text)",
        }}
      >
        {output || <span className="muted">(waiting for audio…)</span>}
      </pre>

      {error && (
        <p className="small" style={{ color: "var(--danger)", marginTop: 10 }}>
          {error}
        </p>
      )}
    </div>
  );
}
